import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { useAlert } from "@web-app/components";
import {
  AppFilterType,
  CreateBulkOrdersPayload,
  DeleteBulkOrdersPayload,
  Order,
  UpdateBulkOrdersPayload,
} from "@web-app/types";
import { handleMutationError } from "@web-app/utils/errorHandler";
import { createOrderRouteType } from "@web-app/utils/order";

import { useOrdersCache } from "../cache/orders";
import {
  copy,
  create,
  createBulkOrders,
  createPublic,
  deleteBulkOrders,
  deleteOrder,
  getOne,
  getPaginatedOrders,
  getTrackingOrder,
  update,
  updateBulkOrders,
  updateTrackingOrder,
  verifyTrackingOrderPhone,
} from "../services/orders";

import { useOrders } from "./useOrders";
import { useRouteMutation } from "./useRoute";
import { useInvalidateOrderTimeline } from "./useTimeline";

export const useOrderMutation = () => {
  const ordersCache = useOrdersCache();
  const invalidateOrderTimeline = useInvalidateOrderTimeline();

  const createOrder = useMutation({
    mutationFn: async (payload: z.infer<typeof createOrderRouteType>) => {
      const order = await create(payload);
      return order;
    },
    onSuccess: () => {
      ordersCache.invalidateOrdersCache();
    },
  });

  const updateOrder = useMutation({
    mutationFn: async (params: Partial<Order>) => {
      const response = await update(params?.id || "", params);
      return response;
    },
    onError: (err: unknown) => {
      handleMutationError(err, "update order");
    },
    onSuccess: (updatedOrder) => {
      ordersCache.upsertOrderInCache(updatedOrder);
      // Timeline events are written in the background, so a realtime
      // orderUpdated event refreshes an open timeline once they land. This
      // makes sure the next time the Timeline tab opens it refetches.
      invalidateOrderTimeline(updatedOrder.id);
    },
  });

  const createPublicOrder = useMutation({
    mutationFn: async (payload: z.infer<typeof createOrderRouteType>) => {
      const order = await createPublic(payload);
      return order;
    },
  });

  const copyOrder = useMutation({
    mutationFn: async (id: string) => {
      const response = await copy(id);
      return response;
    },
    onSuccess: (copiedOrder) => {
      ordersCache.upsertOrderInCache(copiedOrder);
    },
  });

  const getOneOrder = useMutation({
    mutationFn: async (id: string) => {
      const response = await getOne(id);
      return response;
    },
  });

  const deleteOrderById = useMutation({
    mutationFn: async (id: string) => {
      await deleteOrder(id);
      return id;
    },
    onSuccess: (id) => {
      ordersCache.removeOrderFromCache(id);
    },
  });

  return {
    copyOrder,
    createOrder,
    createPublicOrder,
    deleteOrderById,
    getOneOrder,
    updateOrder,
  };
};

/**
 * Public order mutation hook - doesn't require authentication or cache
 * Use this for public order forms to avoid session user API calls
 */
export const usePublicOrderMutation = () => {
  const createPublicOrder = useMutation({
    mutationFn: async (payload: z.infer<typeof createOrderRouteType>) => {
      const order = await createPublic(payload);
      return order;
    },
  });

  return {
    createPublicOrder,
  };
};

export const useBulkOrderMutation = () => {
  const ordersCache = useOrdersCache();

  const create = useMutation({
    mutationFn: async (payload: CreateBulkOrdersPayload) => {
      const response = await createBulkOrders(payload);
      return response;
    },
  });

  const update = useMutation({
    mutationFn: async (payload: UpdateBulkOrdersPayload) => {
      const response = await updateBulkOrders(payload);
      return response;
    },
    onSuccess: () => {
      ordersCache.invalidateOrdersCache();
    },
  });

  const remove = useMutation({
    mutationFn: async (payload: DeleteBulkOrdersPayload) => {
      const response = await deleteBulkOrders(payload);
      return response;
    },
    onSuccess: () => {
      ordersCache.invalidateOrdersCache();
    },
  });
  return { create, remove, update };
};

export const useGetTrackingOrder = (id: string) => {
  return useQuery({
    queryFn: async () => await getTrackingOrder(id),
    queryKey: ["trackingOrder", id],
    refetchOnWindowFocus: false,
  });
};

export const useUpdateTrackingOrder = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      payload,
    }: {
      id: string;
      payload: {
        dropoff?: { note: string };
        phoneNumber: string;
        pickup?: { note: string };
      };
    }) => {
      return await updateTrackingOrder(id, payload);
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["trackingOrder", variables.id],
      });
      queryClient.setQueryData(["trackingOrder", variables.id], data);
    },
  });
};

export const useVerifyTrackingOrderPhone = () => {
  return useMutation({
    mutationFn: async ({
      countryCode,
      id,
      phoneNumber,
    }: {
      countryCode: string;
      id: string;
      phoneNumber: string;
    }) => {
      return await verifyTrackingOrderPhone(id, { countryCode, phoneNumber });
    },
  });
};

export const useManagePageOrders = ({
  appFilter,
  searchText = "",
  sortParam = "",
  limit,
  skip,
}: {
  appFilter: AppFilterType;
  limit?: number;
  searchText?: string;
  skip?: number;
  sortParam?: string;
}) => {
  return useQuery({
    queryFn: async ({ signal }) => {
      // appFilter dates are already in UTC, no conversion needed
      return await getPaginatedOrders({
        payload: { ...appFilter, limit, searchText, skip, sortParam },
        signal,
      });
    },
    queryKey: [
      "orders-paginated",
      appFilter,
      searchText,
      sortParam,
      limit,
      skip,
    ],
    refetchOnWindowFocus: false,
  });
};

export function useOrder() {
  const { copyOrder, updateOrder, deleteOrderById } = useOrderMutation();
  const alert = useAlert();
  const { showAlert } = alert;
  const { data } = useOrders();
  const orders = data?.orders;

  const { updateRouteV2 } = useRouteMutation();

  const onDelete = async (orderId: string) => {
    if (!orders) {
      return;
    }

    const order = orders.get(orderId);

    if (!order) {
      return;
    }

    const orderDeliveryType = order.type;
    if (order.route) {
      const route = order.route;
      const routeOrders = route.ordersOrder.map(({ id }) => orders.get(id));
      const pickupOrderCount = routeOrders.filter(
        (order) => order?.type === "ROUTE_OPT_PICKUP",
      );
      const dropoffOrderCount = routeOrders.filter(
        (order) => order?.type === "ROUTE_OPT_DROPOFF",
      );

      if (
        pickupOrderCount.length === 1 &&
        orderDeliveryType === "ROUTE_OPT_PICKUP"
      ) {
        showAlert({
          message: "Need atleast one Pickup order!",
          title: "Error",
          type: "error",
        });
        return;
      }

      if (
        dropoffOrderCount.length === 1 &&
        orderDeliveryType === "ROUTE_OPT_DROPOFF"
      ) {
        showAlert({
          message: "Need atleast one Drop-off order!",
          title: "Error",
          type: "error",
        });
        return;
      }
    }

    const deleteOrder = deleteOrderById.mutateAsync(orderId);

    await alert.showAlertAsync(deleteOrder, {
      error: {
        message: "Something went wrong, please try again!",
        title: "Error",
        type: "error",
      },
      loading: {
        message: "Deleting Order ...",
        title: "Deleting",
        type: "default",
      },
      success: {
        message: "Successfully Deleted Order",
        title: "Success",
        type: "success",
      },
    });

    if (order?.route?.id) {
      deleteOrder.then(() => {
        const route = order.route;

        if (!route) {
          return;
        }

        updateRouteV2.mutateAsync({
          ordersOrder: route.ordersOrder
            .filter((item) => item.id !== order.id)
            .map((data, index) => ({
              id: data.id,
              index,
              type: "order",
            })),
          routeId: route?.id,
        });
      });
    }
  };

  const onCopy = async (orderId: string) => {
    try {
      await alert.showAlertAsync(copyOrder.mutateAsync(orderId), {
        error: {
          message: "Something went wrong, please try again!",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Please wait...",
          title: "Copying order ...",
          type: "default",
        },
        success: {
          message: "Successfully copied order",
          title: "Success",
          type: "success",
        },
      });
    } catch (error) {
      console.error(error);
      alert.showAlert({
        message: "Something went wrong, please try again!",
        title: "Error",
        type: "error",
      });
    }
  };

  const onShare = async (orderId: string, connectedWorkspaceId: string) => {
    await alert.showAlertAsync(
      updateOrder.mutateAsync({
        connectedWorkspaceId,
        id: orderId,
      }),
      {
        error: {
          message: "Something went wrong, please try again!",
          title: "Error",
          type: "error",
        },
        loading: {
          message: "Sending Order ...",
          title: "Sending",
          type: "default",
        },
        success: {
          message: "Successfully Sent Order",
          title: "Success",
          type: "success",
        },
      },
    );
  };

  return {
    onCopy,
    onDelete,
    onShare,
  };
}
